import { useEffect, useRef, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { recordDiagnostic } from '@/features/diagnostics';
import type { PageImageTransform } from '@/sources/types';

/**
 * How many automatic retries the `<img>` `onerror` path will perform before
 * giving up and surfacing the "Couldn't load page / Retry" UI. We see
 * H@H peers fail often enough at e-hentai that a single retry isn't always
 * sufficient — bumped from 1 to 2 alongside the B2 fix.
 */
const MAX_RETRIES = 2;

export interface PageImageProps {
  /**
   * Resolver invoked lazily and on retry. Returns the CDN URL or `undefined`
   * to give up. Re-calling lets the source re-derive H@H URLs that may have
   * expired between resolve and render.
   */
  resolveUrl: () => Promise<string | undefined>;
  alt?: string;
  /** Page index (1-based) — used for skeleton/error labels. */
  page: number;
  /** Optional fixed style (e.g. width or height constraint). */
  style?: CSSProperties;
  className?: string;
  transform?: PageImageTransform;
  /** Tells PageImage to start loading right now. Defaults to `true`. */
  eager?: boolean;
  /** Fired once the image finishes loading. */
  onLoaded?: () => void;
  /**
   * Called BEFORE every retry attempt (both the automatic `<img>` `onerror`
   * path and the manual "Retry" button). The reader uses it to invalidate
   * the per-page URL cache so the next `resolveUrl()` call hits the source
   * again instead of returning the stale (e.g. expired H@H peer) URL.
   *
   * Without this, retries silently re-mount the same dead `<img src>` and
   * the user sees no improvement no matter how many times they click.
   */
  onRetry?: () => void;
}

/**
 * `<img>` wrapper with retry, blur placeholder, and a small failure mode UI.
 *
 * The image is rendered with `referrerPolicy="no-referrer"` because some
 * source CDNs hotlink-protect against unfamiliar referrers, and our origin
 * is exactly that.
 */
export function PageImage({
  resolveUrl,
  alt,
  page,
  style,
  className,
  transform,
  eager = true,
  onLoaded,
  onRetry,
}: PageImageProps) {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [attempt, setAttempt] = useState(0);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | undefined>(undefined);
  const [displayWidth, setDisplayWidth] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const resolverRef = useRef(resolveUrl);
  resolverRef.current = resolveUrl;
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;
  const rowReverseTransform = isRowReverseTransform(transform) ? transform : undefined;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !rowReverseTransform) return;
    const updateWidth = () => {
      const width = el.getBoundingClientRect().width;
      if (width > 0) setDisplayWidth(width);
    };
    updateWidth();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rowReverseTransform]);

  // Resolve a URL on mount + on retry.
  useEffect(() => {
    if (!eager) {
      return;
    }
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setStatus('loaded');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    resolverRef
      .current()
      .then((url) => {
        if (cancelled) return;
        if (!url) {
          recordDiagnostic('image', 'resolve-empty', { page }, 'warn');
          setStatus('error');
          return;
        }
        setNaturalSize(undefined);
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) {
          recordDiagnostic('image', 'resolve-error', { page }, 'error');
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, eager, page]);

  function handleError() {
    recordDiagnostic('image', 'load-error', { page, attempt }, 'warn');
    if (attempt < MAX_RETRIES) {
      // Drop the cached URL on the parent so the next resolver call hits
      // the source again. H@H peer URLs are short-lived; re-resolving is
      // the only thing that makes a retry meaningful.
      onRetryRef.current?.();
      setSrc(undefined);
      setAttempt((a) => a + 1);
    } else {
      setStatus('error');
    }
  }

  function handleLoad(event: SyntheticEvent<HTMLImageElement>) {
    recordDiagnostic('image', 'load-success', { page, attempt });
    setNaturalSize({
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    });
    setStatus('loaded');
    onLoaded?.();
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex w-full items-center justify-center overflow-hidden bg-muted/40',
        className,
      )}
      style={style}
    >
      {status !== 'loaded' && status !== 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-[10px] uppercase tracking-wide">Loading page {page}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center text-muted-foreground">
          <ImageOff className="h-6 w-6 opacity-50" />
          <span className="text-xs">Couldn&apos;t load page {page}</span>
          <button
            type="button"
            onClick={() => {
              onRetryRef.current?.();
              setStatus('loading');
              setAttempt((a) => a + 1);
            }}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          >
            Retry
          </button>
        </div>
      )}
      {src && (
        rowReverseTransform ? (
          <>
            <img
              ref={imgRef}
              src={src}
              alt={alt ?? `Page ${page}`}
              aria-hidden="true"
              referrerPolicy="no-referrer"
              draggable={false}
              onLoad={handleLoad}
              onError={handleError}
              className="pointer-events-none absolute h-auto max-w-full opacity-0"
            />
            {status === 'loaded' && naturalSize && displayWidth !== undefined && (
              <RowReverseImage
                src={src}
                alt={alt ?? `Page ${page}`}
                rows={rowReverseTransform.rows}
                naturalSize={naturalSize}
                displayWidth={displayWidth}
              />
            )}
          </>
        ) : (
          <img
            ref={imgRef}
            src={src}
            alt={alt ?? `Page ${page}`}
            referrerPolicy="no-referrer"
            draggable={false}
            onLoad={handleLoad}
            onError={handleError}
            className={cn(
              'h-auto max-w-full object-contain',
              status !== 'loaded' && 'opacity-0',
              status === 'loaded' && 'opacity-100',
            )}
          />
        )
      )}
    </div>
  );
}

function isRowReverseTransform(value: PageImageProps['transform']): value is PageImageTransform {
  return value?.kind === 'row-reverse' && Number.isInteger(value.rows) && value.rows > 1;
}

interface RowReverseImageProps {
  src: string;
  alt: string;
  rows: number;
  naturalSize: { width: number; height: number };
  displayWidth: number;
}

function RowReverseImage({ src, alt, rows, naturalSize, displayWidth }: RowReverseImageProps) {
  const scale = displayWidth / naturalSize.width;
  const displayHeight = naturalSize.height * scale;
  const blocks = rowBlocks(naturalSize.height, rows).reverse();
  let destY = 0;
  return (
    <div
      role="img"
      aria-label={alt}
      data-image-transform="row-reverse"
      data-transform-rows={rows}
      className="relative max-w-full overflow-hidden bg-background"
      style={{ width: displayWidth, height: displayHeight }}
    >
      {blocks.map((block, destIndex) => {
        const top = destY * scale;
        const height = block.height * scale;
        destY += block.height;
        return (
          <div
            key={`${block.sourceRow}-${destIndex}`}
            data-jm-slice
            data-source-row={block.sourceRow}
            data-dest-row={destIndex + 1}
            className="absolute left-0 overflow-hidden"
            style={{ top, width: displayWidth, height }}
          >
            <img
              src={src}
              alt=""
              aria-hidden="true"
              referrerPolicy="no-referrer"
              draggable={false}
              className="max-w-none"
              style={{
                width: displayWidth,
                height: displayHeight,
                transform: `translateY(-${block.start * scale}px)`,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function rowBlocks(height: number, rows: number): Array<{ sourceRow: number; start: number; height: number }> {
  const blockSize = Math.floor(height / rows);
  const remainder = height % rows;
  return Array.from({ length: rows }, (_, index) => {
    const start = index * blockSize;
    const rowHeight = blockSize + (index === rows - 1 ? remainder : 0);
    return { sourceRow: index + 1, start, height: rowHeight };
  });
}
